import type { ID } from '@octopus-do/octopus-project-core'

import type { LayoutEdge, Point } from './types'

// Default turns into the first and last child with ChildrenCorner: a 10px
// outer border-radius on a 2px line, i.e. 9px along the line's centre.
const CORNER_RADIUS = 9

const length = ([ax, ay]: Point, [bx, by]: Point) =>
  Math.abs(bx - ax) + Math.abs(by - ay)

// Segments are axis-aligned. A straight run or a zero-length leg keeps a
// plain vertex.
const toPath = (points: Point[], corner: number) =>
  points
    .map(([x, y], i) => {
      if (i !== corner) {
        return `${i ? 'L' : 'M'}${x} ${y}`
      }
      const prev = points[i - 1]
      const next = points[i + 1]
      const r = Math.min(
        CORNER_RADIUS,
        length(prev, [x, y]),
        length([x, y], next)
      )
      const ux = Math.sign(x - prev[0])
      const uy = Math.sign(y - prev[1])
      const vx = Math.sign(next[0] - x)
      const vy = Math.sign(next[1] - y)
      const cross = ux * vy - uy * vx
      if (!r || !cross) {
        return `L${x} ${y}`
      }
      return `L${x - ux * r} ${y - uy * r} A${r} ${r} 0 0 ${cross > 0 ? 1 : 0} ${x + vx * r} ${y + vy * r}`
    })
    .join(' ')

// Siblings share their parent's stem and bar. Only the two ends of the bar
// turn a corner; the stem and the middle children join it in a T, as in
// Default.
export const edgePaths = (edges: LayoutEdge[]): string[] => {
  const ends = new Map<ID, [number, number]>()
  for (const { from, points } of edges) {
    const [x] = points[points.length - 1]
    const [min, max] = ends.get(from) ?? [x, x]
    ends.set(from, [Math.min(min, x), Math.max(max, x)])
  }
  return edges.map(({ from, points }) => {
    const [min, max] = ends.get(from) ?? [0, 0]
    const [x] = points[points.length - 1]
    const outer = min !== max && (x === min || x === max)
    return toPath(points, outer ? points.length - 2 : -1)
  })
}
